export const CHECKOUT_PLAN_KEYS = Object.freeze({
    UNDER_100K: "under-100k",
    OVER_100K: "over-100k",
});

export const CHECKOUT_BILLING_TERMS = Object.freeze({
    MONTHLY: "monthly",
    ANNUAL: "annual",
});

const PADDLE_SCRIPT_SRC = "https://cdn.paddle.com/paddle/paddle.js";
let paddleClientPromise = null;

function buildPriceMetadata(priceData) {
    const isUK = priceData.country === "GB";
    const vatLabel = isUK ? " inc. VAT" : "";
    const recurringPrice = priceData.recurring.price;
    const displayPrice = isUK ? recurringPrice.gross : recurringPrice.net;

    return {
        displayPrice,
        vatLabel,
        grossPrice: recurringPrice.gross,
        netPrice: recurringPrice.net,
        taxPrice: recurringPrice.tax,
    };
}

export function getPaddleClient() {
    return window.Paddle;
}

export function loadPaddleClient() {
    const existingPaddleClient = getPaddleClient();
    if (existingPaddleClient) {
        return Promise.resolve(existingPaddleClient);
    }

    if (paddleClientPromise) {
        return paddleClientPromise;
    }

    paddleClientPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${PADDLE_SCRIPT_SRC}"]`);
        const paddleScript = existingScript ?? document.createElement("script");

        function handleLoad() {
            cleanup();
            if (getPaddleClient()) {
                resolve(getPaddleClient());
                return;
            }
            reject(new Error("Paddle loaded without exposing a client."));
        }

        function handleError() {
            cleanup();
            reject(new Error("Failed to load Paddle script."));
        }

        function cleanup() {
            paddleScript.removeEventListener("load", handleLoad);
            paddleScript.removeEventListener("error", handleError);
            paddleClientPromise = null;
        }

        paddleScript.addEventListener("load", handleLoad);
        paddleScript.addEventListener("error", handleError);

        if (!existingScript) {
            paddleScript.src = PADDLE_SCRIPT_SRC;
            paddleScript.async = true;
            document.body.appendChild(paddleScript);
        }
    });

    return paddleClientPromise;
}

export function getCheckoutPlanDefinitions() {
    return [
        {
            key: CHECKOUT_PLAN_KEYS.UNDER_100K,
            title: "Companies under $100k USD revenue",
            eyebrow: "Paid plan",
            description: "For smaller studios and companies with under $100k USD in annual revenue.",
            selectionLabel: "Live pricing",
            features: [
                // "Built for smaller-scale companies, startups, and growing studios",
                // "20 GB cloud storage",
            ],
            isPurchasable: true,
            tier: 1,
        },
        {
            key: CHECKOUT_PLAN_KEYS.OVER_100K,
            title: "Companies over $100k USD revenue",
            eyebrow: "Paid plan",
            description: "For established studios and companies with over $100k USD in annual revenue.",
            selectionLabel: "Live pricing",
            features: [
                // "Designed for established companies and larger production teams",
                // "20 GB cloud storage",
            ],
            isPurchasable: true,
            tier: 2,
        },
    ];
}

export function getTierProductConfig(planKey) {
    if (planKey === CHECKOUT_PLAN_KEYS.UNDER_100K) {
        return {
            key: CHECKOUT_PLAN_KEYS.UNDER_100K,
            title: "Indie",
            monthly: process.env.REACT_APP_PADDLE_INDIE_MONTHLY_PLAN_ID,
            yearly: process.env.REACT_APP_PADDLE_INDIE_ANNUAL_PLAN_ID,
            tier: 1,
        };
    }

    if (planKey === CHECKOUT_PLAN_KEYS.OVER_100K) {
        return {
            key: CHECKOUT_PLAN_KEYS.OVER_100K,
            title: "Pro",
            monthly: process.env.REACT_APP_PADDLE_PRO_MONTHLY_PLAN_ID,
            yearly: process.env.REACT_APP_PADDLE_PRO_ANNUAL_PLAN_ID,
            tier: 2,
        };
    }

    return null;
}

export function getPurchasablePlanConfigs() {
    return [
        getTierProductConfig(CHECKOUT_PLAN_KEYS.UNDER_100K),
        getTierProductConfig(CHECKOUT_PLAN_KEYS.OVER_100K),
    ].filter((plan) => {
        return !!plan?.monthly && !!plan?.yearly;
    });
}

export function initializePaddleCheckout(paddleClient) {
    if (!paddleClient) {
        return false;
    }

    if (process.env.REACT_APP_STATE === "development") {
        paddleClient.Environment.set("sandbox");
    }

    const vendorID = Number.parseInt(process.env.REACT_APP_PADDLE_VENDOR_ID, 10);
    if (!Number.isFinite(vendorID)) {
        return false;
    }

    paddleClient.Setup({
        vendor: vendorID,
    });
    return true;
}

export function getMonthlyAmountFromYearlyPrice(price) {
    const currencySymbol = price.match(/^\D+/)?.[0] ?? "";
    const numericValue = Number.parseFloat(price.replace(/[^\d.-]/g, ""));
    if (Number.isNaN(numericValue)) {
        return `${currencySymbol}0.00`;
    }
    const monthly = numericValue / 12;
    return `${currencySymbol}${monthly.toFixed(2)}`;
}

export function fetchPaddleProductPrice(paddleClient, productId) {
    return new Promise((resolve) => {
        paddleClient.Product.Prices(productId, (priceData) => {
            resolve(priceData);
        });
    });
}

export function mapMonthlyDisplayPrice(priceData, productConfig) {
    const {
        displayPrice,
        vatLabel,
        grossPrice,
        netPrice,
        taxPrice,
    } = buildPriceMetadata(priceData);

    return {
        displayValue: `${displayPrice}/m${vatLabel}`,
        billingValue: displayPrice,
        billingLabel: `Billed monthly${vatLabel}`,
        productName: `Animotive ${productConfig.title} (Monthly)`,
        productID: productConfig.monthly,
        productPrice: grossPrice,
        productTaxFree: netPrice,
        productTax: taxPrice,
    };
}

export function mapYearlyDisplayPrice(priceData, productConfig) {
    const {
        displayPrice,
        vatLabel,
        grossPrice,
        netPrice,
        taxPrice,
    } = buildPriceMetadata(priceData);

    return {
        displayValue: `${getMonthlyAmountFromYearlyPrice(displayPrice)}/m${vatLabel}`,
        billingValue: displayPrice,
        billingLabel: `Billed annually${vatLabel}`,
        productName: `Animotive ${productConfig.title} (Annual)`,
        productID: productConfig.yearly,
        productPrice: grossPrice,
        productTaxFree: netPrice,
        productTax: taxPrice,
    };
}

export function openPaddleCheckout({
    paddleClient,
    productID,
    tier,
    emailAddress,
    onSuccess,
}) {
    if (!paddleClient) {
        return;
    }

    paddleClient.Checkout.open({
        method: "overlay",
        product: productID,
        email: emailAddress,
        quantity: 1,
        allowQuantity: false,
        passthrough: `{"userTier":"${tier}"}`,
        disableLogout: true,
        successCallback: onSuccess,
    });
}
